import React from 'react';
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import { RefreshOutlined as RefreshIcon, LoginOutlined as LoginIcon } from '@mui/icons-material';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  errorType: 'auth' | 'network' | 'unknown';
  errorMessage: string;
}

class SessionErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorType: 'unknown',
      errorMessage: ''
    };
  }

  static getDerivedStateFromError(error: Error): State {
    console.error('SessionErrorBoundary caught error:', error);
    
    // Categorize the error
    let errorType: 'auth' | 'network' | 'unknown' = 'unknown';
    
    if (error.message.includes('401') || error.message.includes('unauthorized') || error.message.includes('authentication')) {
      errorType = 'auth';
    } else if (error.message.includes('network') || error.message.includes('fetch')) {
      errorType = 'network';
    }
    
    return {
      hasError: true,
      errorType,
      errorMessage: error.message
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Session error details:', error, errorInfo);
    
    // If it's an auth error, clear localStorage
    if (this.state.errorType === 'auth') {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoToLogin = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            p: 4,
            bgcolor: 'grey.50'
          }}
        >
          <Alert 
            severity="error" 
            sx={{ maxWidth: 600, mb: 3 }}
          >
            <AlertTitle>
              {this.state.errorType === 'auth' && '🔒 Error de Autenticación'}
              {this.state.errorType === 'network' && '🌐 Error de Conexión'}
              {this.state.errorType === 'unknown' && '⚠️ Error del Sistema'}
            </AlertTitle>
            
            {this.state.errorType === 'auth' && (
              <Typography>
                Tu sesión ha expirado o hay un problema de autenticación. 
                Por favor, inicia sesión nuevamente.
              </Typography>
            )}
            
            {this.state.errorType === 'network' && (
              <Typography>
                Hay un problema de conexión con el servidor. 
                Verifica tu conexión a internet e intenta de nuevo.
              </Typography>
            )}
            
            {this.state.errorType === 'unknown' && (
              <Typography>
                Ha ocurrido un error inesperado en el sistema. 
                Intenta recargar la página o contacta al soporte técnico.
              </Typography>
            )}
          </Alert>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {this.state.errorType === 'auth' ? (
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={this.handleGoToLogin}
                size="large"
              >
                Iniciar Sesión
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
                size="large"
              >
                Recargar Página
              </Button>
            )}
            
            {this.state.errorType !== 'auth' && (
              <Button
                variant="outlined"
                startIcon={<LoginIcon />}
                onClick={this.handleGoToLogin}
                size="large"
              >
                Ir al Login
              </Button>
            )}
          </Box>

          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1, maxWidth: 600 }}>
              <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                <strong>Error Details (Dev Only):</strong><br/>
                Type: {this.state.errorType}<br/>
                Message: {this.state.errorMessage}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

export default SessionErrorBoundary;